import { ReactElement } from "react";
import { DashboardSaleSummary } from "../../../types/dashboard.type";
import {
  DashboardPeriod,
  PERIOD_NOUN_LABEL,
} from "../../../utils/dashboardPeriod";
import { formatNumber, numericFontSize } from "../../../utils";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ArrowOutwardOutlinedIcon from "@mui/icons-material/ArrowOutwardOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ProfitAndCostSkeleton from "../Skeleton/ProfitAndCostSkeleton";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface ProfitAndCostHoProps {
  summary: DashboardSaleSummary | undefined;
  loading: boolean;
  error: boolean;
  period: DashboardPeriod;
}

function MetricRow({
  label,
  value,
  percent,
  icon,
  color,
}: {
  label: string;
  value: string;
  percent: number;
  icon: ReactElement;
  color: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 96,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        bgcolor: color + "14",
        border: `1px solid ${color}40`,
        borderRadius: 3,
        p: 2,
        [compact]: { p: 1.5 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography color="text.secondary" variant="body2">
          {label}
        </Typography>
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            bgcolor: color + "1A",
            "& svg": { fontSize: 16 },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography
          fontWeight={700}
          title={value}
          sx={{
            color,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: numericFontSize(value),
          }}
        >
          {value}
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ flexShrink: 0 }}
        >
          {percent ? `(${percent}%)` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ProfitAndCostHo({
  summary,
  loading,
  error,
  period,
}: ProfitAndCostHoProps) {
  if (loading) return <ProfitAndCostSkeleton />;

  const nounLabel = PERIOD_NOUN_LABEL[period];

  return (
    <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="body1" fontWeight={600} mb={2}>
        ข้อมูลต้นทุน/กำไรขั้นต้นของ{nounLabel}
      </Typography>

      {error || !summary ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="error" fontWeight={500}>
            ไม่สามารถโหลดข้อมูลได้
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <MetricRow
            label="ต้นทุนสินค้า"
            value={`฿${formatNumber(summary.total_product_cost, { decimals: 0 })}`}
            percent={summary.total_product_cost_percent}
            icon={<ArrowOutwardOutlinedIcon />}
            color="#5C69E8"
          />
          <MetricRow
            label="กำไรขั้นต้น"
            value={`฿${formatNumber(summary.total_revenue, { decimals: 0 })}`}
            percent={summary.total_revenue_percent}
            icon={<TrendingUpOutlinedIcon />}
            color="#45be77"
          />
        </Box>
      )}
    </Paper>
  );
}
